import Markdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkMath from "remark-math"
import { type JSX, useEffect, useState } from "react"
import { FileHead } from "./file_head"
import { saveNote, useSave } from "./use_save"

// $inline$ and $$display$$ math render via KaTeX (the stylesheet ships from
// the asset pipeline; see package.json's build script). remark-math only
// recognizes dollar delimiters — \( \) and \[ \] pass through as plain text.

type MarkdownFileProps = {
  name: string
  // Fixed page title for special files (the objective). Absent = show `name`.
  title?: string
  meta: string
  body: string
  onBack?: () => void
  onRename?: (next: string) => Promise<string | null>
  // Absent on sample files — a "save" then only updates local state.
  onSave?: (body: string) => Promise<boolean>
}

// A markdown document. Rendered by default, and the rendered view is inert —
// selecting a run of prose or a rendered formula has to be able to mean
// "copy this", which it cannot if a click on it swaps the page for a textarea.
// Editing is entered deliberately, through Edit in the header, which then
// becomes Cancel/Save. Save persists and returns to the rendered view; Cancel
// (or Escape) discards the draft.
export function MarkdownFile({ name, title, meta, body, onBack, onRename, onSave }: MarkdownFileProps): JSX.Element {
  // What the rendered view shows. Starts as the server's copy and only moves
  // when a save succeeds — cancelling always has this to fall back to.
  const [content, setContent] = useState(body)
  const [draft, setDraft] = useState("")
  const [editing, setEditing] = useState(false)
  const { saving, save } = useSave(onSave)
  const dirty = draft !== content

  // A newer server copy can arrive after mount: the objective is generated in
  // the background, and the pane is already open by the time it lands. Not
  // while the user is editing — their words win over a generated draft, the
  // same rule GenerateWorkspaceObjectiveJob follows.
  useEffect(() => {
    if (!editing) setContent(body)
  }, [body])

  function startEditing(): void {
    setDraft(content)
    setEditing(true)
  }

  function cancel(): void {
    setEditing(false)
  }

  async function commit(): Promise<void> {
    if (onSave && !(await save(draft))) return
    setContent(draft)
    setEditing(false)
  }

  return (
    <article className="workspace__doc workspace__doc--file">
      <FileHead
        name={name}
        title={title}
        onRename={onRename}
        meta={meta}
        note={saveNote(Boolean(onSave))}
        onBack={onBack}
        actions={
          editing ? (
            <>
              <button type="button" className="btn btn--ghost btn--sm" onClick={cancel}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={!dirty || saving}
                onClick={() => void commit()}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm workspace__edit-btn"
              onClick={startEditing}
            >
              <PencilIcon />
              Edit
            </button>
          )
        }
      />

      {editing ? (
        <textarea
          className="workspace__editor"
          aria-label={name}
          spellCheck={false}
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault()
              cancel()
            }
          }}
        />
      ) : (
        <div className="workspace__markdown">
          {content.trim() ? (
            <Markdown remarkPlugins={[ remarkMath ]} rehypePlugins={[ rehypeKatex ]}>{content}</Markdown>
          ) : (
            <p className="workspace__markdown-empty">Nothing here yet — press Edit to write.</p>
          )}
        </div>
      )}
    </article>
  )
}

// Decorative: the button says "Edit" beside it, so there is nothing here for a
// screen reader to announce twice.
function PencilIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  )
}
